import React, { useState, useEffect, useRef } from 'react';

import { useResizeObserver } from 'usehooks-ts';

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import { Table, Flex, Divider } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { LoadingOutlined, ReloadOutlined } from '@ant-design/icons';
import { NotificationInstance } from 'antd/lib/notification/interface';

import Toolbar from '@controls/toolbar/toolbar';
import IBaseComponentProps from '@components/base-component-props';

import { useAppDispatch } from '@store/hooks';

import { exception } from '@extensions/notification';
import { userLoaded } from '@store/actions';

import { IUserCallLog } from '@entities/user-call-log';
import { getDateTimeLocal } from '@extensions/utils';

dayjs.extend(utc);

interface ICallLogHistoryProps extends IBaseComponentProps {
    userId?: string;
    api: NotificationInstance;
}

const CallLogHistory = (props: ICallLogHistoryProps) => {
    const { userId, api, serverFetch } = props;

    const containerRef = useRef<HTMLDivElement>(null);
    const { width = 0, height = 0 } = useResizeObserver({
        ref: containerRef,
        box: 'border-box',
    });

    const d = useAppDispatch();

    const [callLogs, setCallLogs] = useState<Array<IUserCallLog>>();
    const [loadingCallLogs, setLoadingCallLogs] = useState<boolean>(false);
    const [refreshRequired, setRefreshRequired] = useState<boolean>(true);

    useEffect(() => {
        let cleanup = false;

        if (!refreshRequired) return;

        setLoadingCallLogs(true);

        const fetchData = async () => {
            const promises = [
                await serverFetch(`usercalllogs`, { method: 'GET', queryParams: { userId: userId } })
                    .then((data: any) => {
                        return data;
                    })
                    .catch((ex: any) => {
                        exception(api, 'Ошибка получения истории звонков', ex, () => d(userLoaded(undefined)));
                    }),
            ];

            Promise.all([promises]).then((result) => {
                if (cleanup) return;

                setLoadingCallLogs(false);

                setCallLogs(result[0][0]);
                setRefreshRequired(false);
            });
        };

        fetchData();

        return () => {
            cleanup = true;
        };
    }, [refreshRequired]);

    const renderToolbar = () => {
        return (
            <Toolbar
                commands={[
                    {
                        label: 'Обновить',
                        disabled: loadingCallLogs,
                        key: 'refresh',
                        icon: <ReloadOutlined />,
                        onClick: () => {
                            setRefreshRequired(true);
                        },
                    },
                ]}
            />
        );
    };

    const renderTable = () => {
        const columns: ColumnsType<IUserCallLog> = [
            {
                render: (_, record) => {
                    return (
                        <Flex vertical={true} gap='middle'>
                            <Flex align='center' justify='space-between' style={{ fontWeight: 600, color: '#1677ff' }}>
                                <div>{record.managerName}</div>
                                <div>{getDateTimeLocal(record.createdOn)}</div>
                            </Flex>
                            <div style={{ fontStyle: 'italic' }}>{record.comment}</div>
                        </Flex>
                    );
                },
            },
        ];

        return (
            <Table
                showHeader={false}
                virtual={true}
                rowKey='id'
                size='small'
                columns={columns}
                dataSource={callLogs}
                loading={{
                    spinning: loadingCallLogs,
                    indicator: <LoadingOutlined style={{ fontSize: 44 }} spin />,
                }}
                pagination={false}
                scroll={{ y: height ?? 0 }}
            />
        );
    };

    return (
        <div ref={containerRef} style={{ height: `calc(100vh - 150px)` }}>
            {renderToolbar()}
            <Divider style={{ marginTop: 5, marginBottom: 10 }} />
            {renderTable()}
        </div>
    );
};

export default CallLogHistory;
